import { DownloadIcon, theme, useToast } from '@arnold/common';
import styled from '@emotion/styled';
import { FC, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Menu = styled.ul`
  position: absolute;
  top: calc(100% + ${theme.spacing.d});
  right: 0;
  margin: 0;
  padding: ${theme.spacing.d} 0;
  list-style: none;
  min-width: 160px;
  background-color: ${theme.colors.backgroundCover.default};
  border-radius: ${theme.borderRadius.m};
  box-shadow: 0 5px 30px -20px rgb(0 0 0 / 50%);
  z-index: 100;
`;

const MenuItem = styled.li`
  padding: ${theme.spacing.d} ${theme.spacing.f};
  cursor: pointer;
  font-family: ${theme.typography.body.medium.regular.family};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  color: ${theme.colors.text.primary};

  &:hover {
    background-color: ${theme.colors.backgroundBasic.hover};
  }
`;

export const ExportReportButton: FC<{ compact?: boolean }> = ({ compact }) => {
  const { t } = useTranslation('newReport');
  const { addToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleExport = () => {
    setIsOpen(false);
    addToast(t('notImplemented'));
  };

  return (
    <Wrapper ref={wrapperRef} data-icom="new-report-export">
      <StyledButton
        variant="outline-primary"
        size="sm"
        onClick={() => setIsOpen((open) => !open)}
        data-cy="export-report-button"
        compact={compact}
      >
        {t('exportButton')}
        <StyledDownloadIcon />
      </StyledButton>
      {isOpen && (
        <Menu>
          <MenuItem onClick={handleExport} data-cy="export-report-excel">
            {t('excel')}
          </MenuItem>
          <MenuItem onClick={handleExport} data-cy="export-report-powerPoint">
            {t('powerPoint')}
          </MenuItem>
        </Menu>
      )}
    </Wrapper>
  );
};

const StyledDownloadIcon = styled(DownloadIcon)`
  margin-left: ${theme.spacing.c};
`;

const StyledButton = styled(Button)<{ compact?: boolean }>`
  padding: ${(props) => (props.compact ? 0 : `${theme.spacing.d} ${theme.spacing.g}`)};
  border: ${(props) => (props.compact ? 'none' : `${theme.spacing.a} solid ${theme.colors.iconAction.primary}`)};
  &:hover {
    svg path {
      fill: ${theme.colors.textInverted.primary};
    }
  }
`;
